import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';

export default function Dashboard() {
  const { faculty } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ events: 0, totalPasses: 0, activePasses: 0, usedToday: 0 });
  const [recentPasses, setRecentPasses] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/events'),
      api.get('/passes'),
    ]).then(([evRes, passRes]) => {
      const events = evRes.data.events || [];
      const passes = passRes.data.passes || [];
      const today = new Date().toDateString();
      setStats({
        events: events.length,
        totalPasses: passes.length,
        activePasses: passes.filter(p => p.status === 'active').length,
        usedToday: passes.filter(p => p.entryTime && new Date(p.entryTime).toDateString() === today).length,
      });
      setRecentPasses(passes.slice(0, 5));
      setUpcomingEvents(events.filter(e => e.status === 'upcoming').slice(0, 3));
    }).finally(() => setLoading(false));
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">{greeting}, {faculty?.name?.split(' ')[0]}</div>
          <div className="page-subtitle">{faculty?.designation} · {faculty?.department}</div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate('/events')}>New event</button>
          <button type="button" className="btn btn-red" onClick={() => navigate('/passes')}>Issue pass</button>
        </div>
      </div>

      <div className="page-body">
        <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
          {[
            { icon: 'Events', label: 'Total events', value: stats.events },
            { icon: 'Passes', label: 'Total passes', value: stats.totalPasses },
            { icon: 'Active', label: 'Active passes', value: stats.activePasses },
            { icon: 'Today', label: "Today's entries", value: stats.usedToday },
          ].map(s => (
            <div className="stat-card" key={s.label}>
              <div className="stat-icon">{s.icon}</div>
              <div className="stat-value">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="grid-2">
          <div className="card">
            <div className="card-head">
              <h2 className="card-heading">Recent passes</h2>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate('/passes')}>View all</button>
            </div>
            {recentPasses.length === 0 ? (
              <div className="empty-state empty-state-soft">
                <div className="empty-icon">Nothing here</div>
                <div className="empty-text">No passes issued yet</div>
              </div>
            ) : (
              <div className="stack-gap">
                {recentPasses.map(p => (
                  <div key={p._id} className="mini-list-row">
                    <div className="mini-avatar">{p.guestName.charAt(0).toUpperCase()}</div>
                    <div className="mini-list-body">
                      <div className="mini-list-title">{p.guestName}</div>
                      <div className="mini-list-mono">{p.passCode}</div>
                    </div>
                    <span className={`badge badge-${p.status}`}>{p.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-head">
              <h2 className="card-heading">Upcoming events</h2>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate('/events')}>View all</button>
            </div>
            {upcomingEvents.length === 0 ? (
              <div className="empty-state empty-state-soft">
                <div className="empty-icon">Nothing here</div>
                <div className="empty-text">No upcoming events</div>
              </div>
            ) : (
              <div className="stack-gap" style={{ gap: '0.75rem' }}>
                {upcomingEvents.map(e => (
                  <div
                    key={e._id}
                    className="event-preview-card"
                    onClick={() => navigate('/events')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={ev => ev.key === 'Enter' && navigate('/events')}
                  >
                    <div className="event-preview-title-row">
                      <div className="event-preview-name">{e.title}</div>
                      <span className="badge badge-upcoming">{e.type?.replace('_', ' ')}</span>
                    </div>
                    <div className="event-preview-meta">
                      {e.venue} · {new Date(e.date).toLocaleDateString('en-IN')}
                    </div>
                    <div className="event-preview-sub">
                      {e.passCount || 0} / {e.maxGuests} passes issued
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="card cta-banner">
          <div>
            <div className="cta-banner-title">Gate security</div>
            <div className="cta-banner-text">Verify visitor passes with a QR scan or pass code.</div>
          </div>
          <button type="button" className="btn btn-navy btn-lg" onClick={() => navigate('/gate')}>Open gate view</button>
        </div>
      </div>
    </div>
  );
}
